/**
 * Date range picker with two inline date selectors.
 * Supports dropdown (opens above) and panel (opens below) contexts via calendarPosition.
 */

import { cn } from '@/lib/utils';
import { formatToISODate } from '@/utils/dateUtils';

import { DatePicker } from './DatePicker';

interface CustomDateRangeProps {
  /** Start date as ISO string (YYYY-MM-DD) */
  startDate?: string;
  /** End date as ISO string (YYYY-MM-DD) */
  endDate?: string;
  /** Callback when start date changes */
  onStartChange: (date: string | undefined) => void;
  /** Callback when end date changes */
  onEndChange: (date: string | undefined) => void;
  /** Label for start date picker */
  startLabel?: string;
  /** Label for end date picker */
  endLabel?: string;
  /** Additional className for container */
  className?: string;
  /**
   * Calendar position for date pickers.
   * - 'above': Opens above input (default, best for dropdowns)
   * - 'below': Opens below input (best for panels/sheets)
   */
  calendarPosition?: 'above' | 'below';
  /** Whether to show the "Custom range:" label (default: true) */
  showLabel?: boolean;
}

export const CustomDateRange = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  startLabel = 'From',
  endLabel = 'To',
  className,
  calendarPosition = 'above',
  showLabel = true,
}: CustomDateRangeProps) => {
  const handleStartChange = (date: Date | null) => {
    onStartChange(date ? formatToISODate(date) : undefined);
  };

  const handleEndChange = (date: Date | null) => {
    onEndChange(date ? formatToISODate(date) : undefined);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn('space-y-2', className)}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Label - matches other section labels */}
      {showLabel && (
        <span className="text-xs text-zinc-700 dark:text-zinc-300 block">
          Custom range:
        </span>
      )}

      <div className="grid grid-cols-2 gap-2">
        {/* Start date */}
        <div>
          <span className="text-xs text-zinc-700 dark:text-zinc-300 mb-1 block">
            {startLabel}
          </span>
          <DatePicker
            date={startDate || null}
            onChange={handleStartChange}
            placeholder="Start"
            calendarPosition={calendarPosition}
          />
        </div>

        {/* End date - calendar anchors right to avoid panel overflow */}
        <div>
          <span className="text-xs text-zinc-700 dark:text-zinc-300 mb-1 block">
            {endLabel}
          </span>
          <DatePicker
            date={endDate || null}
            onChange={handleEndChange}
            placeholder="End"
            calendarPosition={calendarPosition}
            calendarAnchor="right"
          />
        </div>
      </div>
    </div>
  );
};
